import { memo, ReactNode, useMemo, useState } from "react";
import { useSearchParams } from "react-router";

import { PollingIntervalGroups } from "apollo/constants";
import usePolledQuery from "apollo/usePolledQuery";
import Filters from "components/Filters";
import FiltersContentWrapper from "components/Filters/ContentWrapper";
import { useCachedFilterFields } from "components/Filters/hooks";
import FiltersSidebar from "components/Filters/Sidebar";
import FiltersSplit from "components/Filters/Split";
import { SavedFilterView, SortOption } from "components/Filters/types";
import { getSearchQuery } from "components/SearchInput/helpers";
import { BillingTierFeature, SearchQueryPredicate, SearchSuggestionsOutput } from "types/generated";
import useTierFeature from "views/Account/hooks/useTierFeature";
import useMakeFiltersData from "components/Filters/useMakeFiltersData";

import {
  EXCLUDED_FILTERS,
  FILTER_ITEMS_DICTIONARY,
  FILTERS_ORDER_SETTINGS_KEY,
  INITIAL_SORT_DIRECTION,
  INITIAL_SORT_OPTION,
} from "../../constants";
import { SEARCH_AUDIT_TRAIL_ENTRIES_SUGGESTIONS } from "../../gql";
import { getFilterKey } from "../../helpers";

type AuditTrialEntriesFiltersLayoutProps = {
  children: ReactNode;
  currentSavedView?: SavedFilterView;
  setCurrentSavedView: (view?: SavedFilterView) => unknown;
  predicates: SearchQueryPredicate[];
};

const AuditTrialEntriesFiltersLayout = ({
  children,
  currentSavedView,
  setCurrentSavedView,
  predicates,
}: AuditTrialEntriesFiltersLayoutProps) => {
  const [fields, setFields] = useState<string[] | null>(null);

  const [urlParams] = useSearchParams();
  const searchInput = getSearchQuery(urlParams);

  const isActive = useTierFeature(BillingTierFeature.Auditing);

  const { data: filteringData, loading } = usePolledQuery<{
    searchAuditTrailEntriesSuggestions: SearchSuggestionsOutput;
  }>(SEARCH_AUDIT_TRAIL_ENTRIES_SUGGESTIONS, {
    pollingGroup: PollingIntervalGroups.Lists,
    variables: {
      input: {
        fullTextSearch: searchInput,
        predicates,
        fields,
      },
    },
    skip: !isActive,
  });

  const cachedFiltersData = useCachedFilterFields(
    filteringData?.searchAuditTrailEntriesSuggestions?.fields
  );

  const sortOptions = useMemo((): SortOption[] => {
    return (
      cachedFiltersData
        .filter((field) => field.orderable)
        .map((field) => {
          return {
            value: field.name,
            label: getFilterKey(field.name),
          };
        }) || []
    );
  }, [cachedFiltersData]);

  const { filters, filterOptionsMap } = useMakeFiltersData({
    suggestions: cachedFiltersData,
    dictionary: FILTER_ITEMS_DICTIONARY,
    excludedFilterFields: EXCLUDED_FILTERS,
  });

  const handlePollingActiveSections = (fields: string[]) => {
    if (fields.length === 0) {
      return;
    }

    setFields(fields);
  };

  return (
    <Filters
      filters={filters}
      filtersItemsOptionsMap={filterOptionsMap}
      filtersLoading={loading}
      sortOptions={sortOptions}
      initialSortOption={INITIAL_SORT_OPTION}
      initialSortDirection={INITIAL_SORT_DIRECTION}
      filtersDictionary={FILTER_ITEMS_DICTIONARY}
      pollActiveSections={handlePollingActiveSections}
      filtersOrderSettingsKey={FILTERS_ORDER_SETTINGS_KEY}
      filtersType="auditTrailEntries"
      currentSavedView={currentSavedView}
      setCurrentSavedView={setCurrentSavedView}
    >
      <FiltersSplit>
        <FiltersSidebar />

        <FiltersContentWrapper>{children}</FiltersContentWrapper>
      </FiltersSplit>
    </Filters>
  );
};

export default memo(AuditTrialEntriesFiltersLayout);
